// Formatting shared by the admin console's tabs. A figure we do not hold renders "—", never a fabricated 0.
import { compactNumber, formatRelativeTime } from "@cntrl/ui";

export const dash = "—";

export function figure(n: number | null | undefined): string {
  return n === null || n === undefined ? dash : compactNumber(n);
}

export function exact(n: number | null | undefined): string {
  return n === null || n === undefined ? dash : n.toLocaleString();
}

export function ago(iso: string | null | undefined, never = dash): string {
  return formatRelativeTime(iso) ?? never;
}

/** How long until an instant. `formatRelativeTime` only speaks of the past, and answers "just now" for any future time. */
export function until(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return dash;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return dash;
  const mins = Math.round((then - now) / 60_000);
  if (mins <= 0) return "due now";
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}

/** The fields of a sync job that say what it is doing right now. */
interface JobLifecycle {
  state?: string | null;
  queued?: boolean | null;
  nextRunOn?: string | null;
}

/** A job's state in plain words. A forced job waiting for its tick reads "queued"; the engine's "dispatched" is a run
 * in flight. */
export function jobState(job: JobLifecycle): string {
  if (job.state === "dispatched") return "running";
  if (job.queued && job.state === "scheduled") return "queued";
  return job.state ?? dash;
}

/** When a job next runs. A blocked or paused job has no next run, and a running job's stored time is only its lease. */
export function nextRun(job: JobLifecycle, now: number = Date.now()): string {
  const state = jobState(job);
  if (state === "blocked" || state === "paused") return dash;
  if (state === "running") return "running";
  if (state === "queued") return "next tick";
  return until(job.nextRunOn, now);
}

export function day(iso: string | null | undefined): string {
  if (!iso) return dash;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? dash : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const PLATFORM_LABELS: Record<string, string> = {
  spotify: "Spotify",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  apple_music: "Apple Music",
  amazon: "Amazon",
  deezer: "Deezer",
  soundcloud: "SoundCloud",
};

export function platformLabel(key: string | null | undefined): string {
  if (!key) return dash;
  return PLATFORM_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Case-insensitive match of a search box against any of the given strings. */
export function matches(query: string, ...fields: (string | null | undefined)[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f?.toLowerCase().includes(q));
}
